import { useState, useCallback } from "react";

import { IconInfo } from "@frontend/common";

import {
  useArticleInfoQuery,
  useNewInfoReviewRequiredMutation,
} from "@api/articleInfo/useArticleInfo";
import { NewInfoPressData } from "@api/dealsSimilarData/dealsSimilarData.type";

import NewInfoPressTableRow from "./components/NewInfoPressTableRow/NewInfoPressTableRow";
import {
  NEW_INFO_PRESS_INFO_TEXT,
  NEW_INFO_PRESS_TABLE_TITLE,
} from "./constants/newInfoPress.constant";
import * as S from "./NewInfoPressTableContainer.style";
import Checkbox from "../OverlappedCheckBox/OverlappedCheckBox";

const NewInfoPressTableContainer = ({
  targetPressId,
  newInfoPressData,
  isResizing,
}: {
  targetPressId: number;
  newInfoPressData: NewInfoPressData[];
  isResizing: boolean;
}) => {
  const [checkedPressIds, setCheckedPressIds] = useState<Set<number>>(
    new Set(),
  );
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);

  const { data: articleInfo } = useArticleInfoQuery();
  const { mutateAsync: updateNewInfoReviewRequiredStatus } =
    useNewInfoReviewRequiredMutation();

  const isAllChecked =
    newInfoPressData?.length > 0 &&
    newInfoPressData.every((item) => checkedPressIds.has(item.id));

  const handleHeaderCheckboxChange = async () => {
    const updatedReviewInformationList = (
      articleInfo?.reviewInformationList ?? []
    ).map((item) => {
      const isInPressData = newInfoPressData.some(
        (press) => press.id === item.id,
      );
      if (isInPressData) {
        return { ...item, deleted: !isAllChecked };
      }
      return item;
    });

    await updateNewInfoReviewRequiredStatus({
      pressId: targetPressId,
      requiredReviewInformationList: updatedReviewInformationList,
    });

    if (isAllChecked) {
      setCheckedPressIds(new Set());
    } else {
      setCheckedPressIds(new Set(newInfoPressData.map((item) => item.id)));
    }
  };

  const handleInitCheck = useCallback((id: number) => {
    setCheckedPressIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const handleTogglePressId = (id: number) => {
    setCheckedPressIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  return (
    <S.NewInfoPressTableContainer isResizing={isResizing}>
      <S.NewInfoPressTableTitleWrapper>
        <S.NewInfoPressTableTitle>
          {NEW_INFO_PRESS_TABLE_TITLE}
        </S.NewInfoPressTableTitle>
        <S.NewInfoPressTableTitleDescriptionWrapper>
          <IconInfo width={16} height={16} color="#017ABC" />
          <S.TooltipDescription className="tooltip-description">
            <S.NewInfoPressTableTitleDescription>
              {NEW_INFO_PRESS_INFO_TEXT}
            </S.NewInfoPressTableTitleDescription>
          </S.TooltipDescription>
        </S.NewInfoPressTableTitleDescriptionWrapper>
      </S.NewInfoPressTableTitleWrapper>
      <S.NewInfoPressTableWrapper>
        <S.NewInfoPressTableHeader>
          <S.NewInfoPressTablePressId>
            <S.NewInfoPressTableHeadDiv>Press ID</S.NewInfoPressTableHeadDiv>
          </S.NewInfoPressTablePressId>
          <S.NewInfoPressTableNewInfoDetails>
            <S.NewInfoPressTableHeadDiv>
              New Info Details
            </S.NewInfoPressTableHeadDiv>
          </S.NewInfoPressTableNewInfoDetails>
          <S.NewInfoPressTableReviewRequired>
            <S.NewInfoPressTableHeadDiv>
              <Checkbox
                checked={isAllChecked}
                onChangeHandler={handleHeaderCheckboxChange}
              />
              Review Required
            </S.NewInfoPressTableHeadDiv>
          </S.NewInfoPressTableReviewRequired>
        </S.NewInfoPressTableHeader>

        {newInfoPressData?.map((newInfoPressItem) => {
          return (
            <NewInfoPressTableRow
              newInfoPressItem={newInfoPressItem}
              hoveredRow={hoveredRow}
              setHoveredRow={setHoveredRow}
              checkedPressIds={checkedPressIds}
              onTogglePressId={handleTogglePressId}
              onInitCheck={handleInitCheck}
              currentPressId={targetPressId}
              reviewInformationList={articleInfo?.reviewInformationList || []}
            />
          );
        })}
      </S.NewInfoPressTableWrapper>
    </S.NewInfoPressTableContainer>
  );
};

export default NewInfoPressTableContainer;
